import { ChevronLeft, ChevronRight } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  Typography
} from '@mui/material'
import { CalendarPicker } from '@mui/x-date-pickers'
import React from 'react'
import Carousel from 'react-material-ui-carousel'
import { connect } from 'react-redux'
import NivosRTE from 'components/nivos-rte'
import placeholderImage from 'images/demo2.png'
import {
  Operations as AmenityBookingOperations,
  operations as amenityBookingOperations
} from 'states/dux/amenity-booking/operations'
import { Payload as AmenityBookingPayload } from 'states/dux/amenity-booking/types'
import {
  Operations as AmenityOperations,
  operations as amenityOperations
} from 'states/dux/amenity/operations'
import { Payload as AmenityPayload } from 'states/dux/amenity/types'
import {
  Operations as LayoutOperations,
  operations as layoutOperations
} from 'states/dux/layout/operations'
import { Payload as LayoutPayload } from 'states/dux/layout/types'
import {
  Operations as ThemeOperations,
  operations as themeOperations
} from 'states/dux/theme/operations'
import { Payload as ThemePayload } from 'states/dux/theme/types'
import {
  Operations as UserOperations,
  operations as userOperations
} from 'states/dux/user/operations'
import { Payload as UserPayload } from 'states/dux/user/types'
import State from 'states/types'
import { Amenity } from 'types/amenity-booking'
import { NivosImage } from 'types/nivos-image'
import {
  listNavigationControl,
  mapOperationsToDispatchProps,
  objectifyDateStringFields,
  post
} from 'utils/common'

interface StateProps {
  theme: ThemePayload
  layout: LayoutPayload
  amenityBooking: AmenityBookingPayload
  amenity: AmenityPayload
  user: UserPayload
}

interface DispatchProps {
  operations: {
    theme: ThemeOperations
    layout: LayoutOperations
    amenityBooking: AmenityBookingOperations
    amenity: AmenityOperations
    user: UserOperations
  }
}

interface OwnProps {
  selectedAmenity: Amenity
  handleClose?: () => void
  selection?: boolean
}

type AppProps = StateProps & DispatchProps & OwnProps

const AmenityCreateReservationFC: React.FC<any> = (props: AppProps) => {
  const { layout, user, selectedAmenity, operations, amenity, handleClose } =
    props
  const [_selectedAmenity, setSelectedAmenity] =
    React.useState<Amenity>(selectedAmenity)
  const [date, setDate] = React.useState(new Date())
  const [currentListIndex, setIndex] = React.useState(0)

  const CarouselItem: React.FC<{ image: NivosImage }> = props => {
    const { image } = props

    const [error, setError] = React.useState(false)
    const [loading, setLoading] = React.useState(true)

    React.useEffect(() => setError(false), [image])

    return (
      <Box display={'flex'} justifyContent={'center'}>
        {loading && (
          <CircularProgress sx={{ position: 'absolute', top: '40%' }} />
        )}
        <img
          height={'380px'}
          width={'auto'}
          alt={`nivos-amenity-${image.name}`}
          src={error ? (placeholderImage as string) : image.url}
          onLoad={() => setLoading(false)}
          onError={() => {
            setLoading(false)
            setError(true)
          }}
          style={{
            borderRadius: '12%',
            filter: error ? 'grayscale(1) blur(1px)' : 'none',
            maxWidth: '100%',
            objectFit: 'cover',
            objectPosition: '50% 50%'
          }}
        />
      </Box>
    )
  }

  const _handleSubmit = (): void => {
    void post('/booking/amenity/', {
      amenity: _selectedAmenity.id,
      from_date: date.toISOString(),
      to_date: date.toISOString(),
      created_by: user.id
    }).then(data => {
      if (data !== undefined) {
        operations.amenityBooking.add(objectifyDateStringFields(data))
      }
      if (handleClose !== undefined) {
        handleClose()
      }
    })
  }

  React.useEffect(() => {
    if (amenity.data.length > 0) {
      setIndex(amenity.data.findIndex(item => item.id === _selectedAmenity.id))
    }
  }, [_selectedAmenity])

  return (
    <Box width={0.95} padding={1} margin={'auto'}>
      <Grid container>
        <Grid item xs={12}>
          <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
            <IconButton
              onClick={() =>
                setSelectedAmenity(
                  listNavigationControl('left', amenity.data, currentListIndex)
                )
              }
            >
              <ChevronLeft />
            </IconButton>
            <Typography variant={'h4'} align={'center'} minWidth={275}>
              {_selectedAmenity.name}
            </Typography>
            <IconButton
              onClick={() =>
                setSelectedAmenity(
                  listNavigationControl('right', amenity.data, currentListIndex)
                )
              }
            >
              <ChevronRight />
            </IconButton>
          </Box>
        </Grid>
      </Grid>
      <Grid container justifyContent={'center'} spacing={2}>
        <Grid item xs={12} sm={6} md={6} lg={8}>
          <Carousel
            autoPlay={true}
            cycleNavigation={true}
            sx={{
              width: '92%',
              minHeight: '300px',
              marginX: 'auto',
              marginTop: '20px'
            }}
            animation={'slide'}
            navButtonsAlwaysInvisible={true}
            indicators={false}
            duration={3000}
            interval={6000}
          >
            {_selectedAmenity.images.map((img, idx) => (
              <CarouselItem image={img} key={idx} />
            ))}
          </Carousel>
        </Grid>
        {_selectedAmenity.bookable && (
          <Grid item xs={12} sm={6} md={6} lg={4}>
            <Box
              display={'flex'}
              flexDirection={'column'}
              alignItems={'center'}
              width={1}
            >
              <CalendarPicker
                date={date}
                onChange={value => (value != null ? setDate(value) : null)}
              />
              <Button
                variant={'contained'}
                color={'primary'}
                sx={{
                  width: 0.65
                }}
                onClick={() => _handleSubmit()}
              >
                {layout.language.translations.createReservation}
              </Button>
            </Box>
          </Grid>
        )}
      </Grid>
      <Box padding={2}>
        <Typography variant={'h4'} gutterBottom>
          Description:
        </Typography>
        {(_selectedAmenity.descriptionRte === null ||
          _selectedAmenity.descriptionRte.trim() === '') && (
          <Typography>{_selectedAmenity.description}</Typography>
        )}
        {_selectedAmenity.descriptionRte !== null &&
          _selectedAmenity.descriptionRte.trim() !== '' && (
            <NivosRTE
              rteState={_selectedAmenity.descriptionRte}
              setRteState={_ => null}
              readOnly={true}
            />
        )}
      </Box>
    </Box>
  )
}

const mapStateToProps = (state: State): StateProps => {
  return {
    theme: state.theme,
    layout: state.layout,
    amenityBooking: state.amenityBooking,
    amenity: state.amenity,
    user: state.user
  }
}

const mapDispatchToProps = mapOperationsToDispatchProps({
  theme: themeOperations,
  layout: layoutOperations,
  amenityBooking: amenityBookingOperations,
  amenity: amenityOperations,
  user: userOperations
})

const AmenityCreateReservation = connect(
  mapStateToProps,
  mapDispatchToProps
)(AmenityCreateReservationFC) as React.FC<OwnProps>

export default AmenityCreateReservation
